import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, List, Modal, Popconfirm, Space, Switch, Table, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { geoExposureApi } from './geoExposureApi';
import { GeoExposureLayout } from './GeoExposureLayout';

const { Text } = Typography;

const RephraseEditor = ({ query, onSaved }) => {
    const [texts, setTexts] = useState(query.rephrases.map((r) => r.text));
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            onSaved(await geoExposureApi.setRephrases(query.id, texts.filter((t) => t.trim())));
            message.success('Rephrases saved');
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            {texts.map((text, i) => (
                <Input.TextArea key={i} autoSize value={text} onChange={(e) => setTexts(texts.map((t, j) => (j === i ? e.target.value : t)))} />
            ))}
            <Space>
                <Button type="primary" size="small" loading={saving} onClick={save}>Save rephrases</Button>
                <Button size="small" onClick={() => setTexts([...texts, ''])}>Add one</Button>
                <Button size="small" onClick={() => setTexts(query.rephrases.map((r) => r.text))}>Reset</Button>
            </Space>
        </Space>
    );
};

/** The seed queries and the rephrases the model wrote for each; the run asks the rephrases. */
export const GeoExposureQueries = () => {
    const [queries, setQueries] = useState(null);
    const [error, setError] = useState(null);
    const [adding, setAdding] = useState(false);
    const [newText, setNewText] = useState('');
    const [creating, setCreating] = useState(false);
    const [busy, setBusy] = useState({});
    const [editing, setEditing] = useState({});

    const load = () => geoExposureApi.listQueries().then(setQueries).catch((e) => setError(e.message));
    useEffect(() => { load(); }, []);

    const replace = (updated) => setQueries((list) => list.map((q) => (q.id === updated.id ? updated : q)));
    const withBusy = async (id, action) => {
        setBusy((b) => ({ ...b, [id]: true }));
        try {
            await action();
        } catch (e) {
            message.error(e.message);
        } finally {
            setBusy((b) => ({ ...b, [id]: false }));
        }
    };

    const create = async () => {
        setCreating(true);
        try {
            const created = await geoExposureApi.createQuery(newText.trim());
            setQueries((list) => [...(list || []), created]);
            setNewText('');
            setAdding(false);
            message.success('Query added with its rephrases');
        } catch (e) {
            message.error(e.message);
        } finally {
            setCreating(false);
        }
    };

    const columns = [
        { title: 'Query', dataIndex: 'text', key: 'text' },
        { title: 'Rephrases', key: 'count', width: 110, align: 'right', render: (_, q) => q.rephrases.length },
        {
            title: 'In runs',
            key: 'enabled',
            width: 100,
            render: (_, q) => <Switch checked={q.enabled} loading={busy[q.id]} onChange={(enabled) => withBusy(q.id, async () => replace(await geoExposureApi.updateQuery(q.id, { enabled })))} />,
        },
        {
            title: '',
            key: 'actions',
            width: 230,
            render: (_, q) => (
                <Space onClick={(e) => e.stopPropagation()}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => setEditing((m) => ({ ...m, [q.id]: !m[q.id] }))}>{editing[q.id] ? 'Done' : 'Edit'}</Button>
                    <Popconfirm title="Replace the rephrases with new ones from the model?" onConfirm={() => withBusy(q.id, async () => replace(await geoExposureApi.regenerateRephrases(q.id)))}>
                        <Button size="small" icon={<ReloadOutlined />} loading={busy[q.id]}>Regenerate</Button>
                    </Popconfirm>
                    <Popconfirm title="Delete this query? Past answers keep their text." onConfirm={() => withBusy(q.id, async () => { await geoExposureApi.deleteQuery(q.id); setQueries((list) => list.filter((x) => x.id !== q.id)); })}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <GeoExposureLayout title="Queries" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>Add query</Button>}>
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
            <Text type="secondary">Each query is rephrased by the model; a run asks ChatGPT every rephrase, never the seed wording itself. {queries ? `${queries.length} queries, ${queries.reduce((n, q) => n + (q.enabled ? q.rephrases.length : 0), 0)} prompts per run.` : ''}</Text>
            <Table
                style={{ marginTop: 12 }}
                size="small"
                rowKey="id"
                loading={!queries}
                dataSource={queries ?? []}
                columns={columns}
                pagination={false}
                expandable={{
                    expandedRowRender: (q) => editing[q.id]
                        ? <RephraseEditor key={q.rephrases.map((r) => r.id).join()} query={q} onSaved={replace} />
                        : <List size="small" dataSource={q.rephrases} renderItem={(r) => <List.Item style={{ padding: '4px 0' }}>{r.position + 1}. {r.text}</List.Item>} />,
                }}
            />
            <Modal open={adding} title="Add a query" onCancel={() => setAdding(false)} onOk={create} okText="Add and rephrase" confirmLoading={creating} okButtonProps={{ disabled: newText.trim().length < 5 }}>
                <Input.TextArea
                    autoSize={{ minRows: 3 }}
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    placeholder="I'm looking for design ideas for my living room, I want it boho chic"
                />
                <Text type="secondary">The model writes the rephrases right away.</Text>
            </Modal>
        </GeoExposureLayout>
    );
};
